import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import maplibregl, { Map as MlMap, Marker } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { apiGet } from "../api/client";
import InterestToggle, { type Interest } from "../components/InterestToggle";
import TownMultiSelect from "../components/TownMultiSelect";
import {
  DEFAULT_FILTERS,
  type InterestFilter,
  type ListingsFilters,
  type SortOption,
  type StatusFilter,
  useListingsStore,
} from "../stores/listingsStore";

interface Listing {
  listing_id: string;
  source: string;
  address: string;
  town: string;
  status: string;
  list_price: number | null;
  sale_price: number | null;
  sale_date: string | null;
  sqft: number | null;
  beds: number | null;
  baths: number | null;
  lat: number | null;
  lng: number | null;
  photos: string[];
  hero_photo?: string;
  ai_price_assessment?: "underpriced" | "fair" | "overpriced" | "unknown";
  ai_verdict?: string;
  matches_criteria?: number;
  match_score?: number | null;
  match_score_breakdown?: Record<string, { score: number; weight: number }>;
  first_seen_at?: string;
  last_seen_at?: string;
  days_on_zillow?: number | null;
  // Server-derived date the home actually came on market (from the source's
  // price history, or days_on_zillow counted back from the last scrape).
  listed_date?: string | null;
  interest?: Interest;
}

const DEFAULT_CENTER: [number, number] = [-74.16, 40.39];
const DEFAULT_ZOOM = 12;
const DAY_MS = 86_400_000;

// Days between now and when a listing became active. Prefers the server's
// listed_date (a real on-market date from the source's price history), then
// Zillow's days_on_zillow adjusted forward for scrape staleness. first_seen_at
// is a last resort: it's when *we* found the row, not when it was listed, so
// it understates age. Returns null if we have nothing.
function daysOnMarket(l: Listing): number | null {
  if (l.listed_date) {
    return Math.floor((Date.now() - new Date(l.listed_date).getTime()) / DAY_MS);
  }
  if (l.days_on_zillow != null) {
    const lastSeen = l.last_seen_at ? new Date(l.last_seen_at).getTime() : Date.now();
    const drift = Math.max(0, Math.floor((Date.now() - lastSeen) / DAY_MS));
    return l.days_on_zillow + drift;
  }
  if (l.first_seen_at) {
    return Math.floor((Date.now() - new Date(l.first_seen_at).getTime()) / DAY_MS);
  }
  return null;
}

// A single "how recent" axis across mixed statuses: for sold listings, the
// sale date; for everything else, the (approximate) date it came on market.
// Larger = more recent.
function recencyTimestamp(l: Listing): number {
  if (l.status === "sold") {
    return l.sale_date ? new Date(l.sale_date).getTime() : 0;
  }
  const dom = daysOnMarket(l);
  return dom != null ? Date.now() - dom * DAY_MS : 0;
}

function fmtRecencyLabel(l: Listing): string {
  if (l.status === "sold") {
    return l.sale_date ? `Sold ${fmtShortDate(l.sale_date)}` : "";
  }
  const dom = daysOnMarket(l);
  if (dom == null) return "";
  // With only first_seen_at to go on the listing is at least this old, but
  // could be much older — mark it so the number isn't read as exact.
  const approx = !l.listed_date && l.days_on_zillow == null ? "+" : "";
  if (dom <= 0) return approx ? "Listed today or earlier" : "Listed today";
  if (dom === 1) return `Listed 1${approx} day ago`;
  return `Listed ${dom}${approx} days ago`;
}

function fmtShortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const markersByIdRef = useRef<Map<string, Marker>>(new Map());
  const navigate = useNavigate();
  const filters = useListingsStore((s) => s.filters);
  const setFiltersStore = useListingsStore((s) => s.setFilters);
  const resetFilters = useListingsStore((s) => s.resetFilters);
  const highlightedIds = useListingsStore((s) => s.highlightedIds);
  const clearHighlight = useListingsStore((s) => s.clearHighlight);
  const [hoverId, setHoverId] = useState<string | null>(null);

  const q = useQuery<{ listings: Listing[] }>({
    queryKey: ["listings", filters.status],
    queryFn: () => {
      const qs = filters.status && filters.status !== "all" ? `?status=${filters.status}` : "";
      return apiGet(`/api/listings${qs}`);
    },
  });

  const availableTowns = useMemo(() => {
    if (!q.data) return [];
    const set = new Set<string>();
    for (const l of q.data.listings) {
      if (l.town) set.add(l.town);
    }
    return Array.from(set).sort();
  }, [q.data]);

  const filtered = useMemo(() => {
    if (!q.data) return [];
    return q.data.listings
      .map((l) => ({
        ...l,
        lat: numOrNull(l.lat),
        lng: numOrNull(l.lng),
        list_price: numOrNull(l.list_price),
        sale_price: numOrNull(l.sale_price),
        sqft: numOrNull(l.sqft),
        beds: numOrNull(l.beds),
        baths: numOrNull(l.baths),
      }))
      .filter((l) => {
        if (l.lat == null || l.lng == null) return false;
        // "saved" isn't a real listing status — the server already scoped the
        // response to saved rows, so nothing further to check here.
        if (filters.status !== "all" && filters.status !== "saved" && l.status !== filters.status) return false;
        if (filters.town.length > 0 && !filters.town.includes(l.town)) return false;
        if (filters.interest === "hide_uninterested" && l.interest === "uninterested") return false;
        if ((filters.interest === "interested" || filters.interest === "uninterested") && l.interest !== filters.interest) {
          return false;
        }
        const price = l.list_price ?? l.sale_price;
        if (filters.minPrice != null && (price == null || price < filters.minPrice)) return false;
        if (filters.maxPrice != null && (price == null || price > filters.maxPrice)) return false;
        if (filters.minSqft != null && (l.sqft == null || l.sqft < filters.minSqft)) return false;
        if (filters.minBeds != null && (l.beds == null || l.beds < filters.minBeds)) return false;
        if (filters.minBaths != null && (l.baths == null || l.baths < filters.minBaths)) return false;
        if (filters.minSchoolScore != null) {
          const ss = (l as Listing & { school_scores?: { elementary?: number | null; middle?: number | null; high?: number | null } }).school_scores;
          const present = [ss?.elementary, ss?.middle, ss?.high].filter(
            (v): v is number => typeof v === "number",
          );
          if (present.length === 0 || Math.min(...present) < filters.minSchoolScore) return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (filters.sortBy === "recency") {
          return recencyTimestamp(b) - recencyTimestamp(a);
        }
        // "Best match": weighted match score desc (unscored listings last),
        // then matching-criteria flag, then price as a final tiebreak.
        const as = a.match_score ?? -1;
        const bs = b.match_score ?? -1;
        if (as !== bs) return bs - as;
        const am = a.matches_criteria ? 1 : 0;
        const bm = b.matches_criteria ? 1 : 0;
        if (am !== bm) return bm - am;
        return (b.list_price ?? b.sale_price ?? 0) - (a.list_price ?? a.sale_price ?? 0);
      });
  }, [q.data, filters]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    markersByIdRef.current.forEach((m) => m.remove());
    markersByIdRef.current = new Map();

    if (filtered.length === 0) return;

    const bounds = new maplibregl.LngLatBounds();

    for (const l of filtered) {
      if (l.lat == null || l.lng == null) continue;

      const el = document.createElement("button");
      el.type = "button";
      el.className = `price-marker price-marker--${l.status}`;
      el.dataset.listingId = l.listing_id;
      el.textContent = formatPrice(l.list_price ?? l.sale_price);
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        navigate(`/listings/${l.listing_id}`);
      });

      const popup = new maplibregl.Popup({
        offset: 16,
        closeButton: false,
        closeOnClick: false,
        className: "listing-popup",
      }).setHTML(renderPopupHtml(l));

      const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
        .setLngLat([l.lng, l.lat])
        .setPopup(popup)
        .addTo(map);

      el.addEventListener("mouseenter", () => {
        setHoverId(l.listing_id);
        marker.togglePopup();
      });
      el.addEventListener("mouseleave", () => {
        setHoverId(null);
        marker.togglePopup();
      });

      markersByIdRef.current.set(l.listing_id, marker);
      bounds.extend([l.lng, l.lat]);
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
    }
  }, [filtered, navigate]);

  // Highlight the marker for the currently-hovered sidebar row, and for any
  // listings flagged via the chat-driven highlight set.
  useEffect(() => {
    markersByIdRef.current.forEach((m, id) => {
      const el = m.getElement();
      el.classList.toggle("price-marker--hover", id === hoverId);
      el.classList.toggle("price-marker--highlighted", highlightedIds.has(id));
    });
  }, [hoverId, highlightedIds]);

  function update<K extends keyof ListingsFilters>(key: K, value: ListingsFilters[K]) {
    setFiltersStore({ [key]: value } as Partial<ListingsFilters>);
  }
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);
  const hasHighlights = highlightedIds.size > 0;

  return (
    <>
      <div className="row" style={{ marginBottom: 12 }}>
        <h1 style={{ margin: 0 }}>Listings</h1>
        <span style={{ flex: 1 }} />
        <span className="muted">{filtered.length} listings</span>
      </div>

      <div
        className="panel"
        style={{ marginBottom: 12, padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}
      >
        <FilterField label="Status">
          <select value={filters.status} onChange={(e) => update("status", e.target.value as StatusFilter)}>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="sold">Sold</option>
            <option value="all">All</option>
            <option value="saved">Saved</option>
          </select>
        </FilterField>
        <FilterField label="Town">
          <TownMultiSelect towns={availableTowns} selected={filters.town} onChange={(towns) => update("town", towns)} />
        </FilterField>
        <FilterField label="Interest">
          <select value={filters.interest} onChange={(e) => update("interest", e.target.value as InterestFilter)}>
            <option value="hide_uninterested">Hide not interested</option>
            <option value="interested">Interested only</option>
            <option value="uninterested">Not interested only</option>
            <option value="all">All</option>
          </select>
        </FilterField>
        <FilterField label="Sort">
          <select value={filters.sortBy} onChange={(e) => update("sortBy", e.target.value as SortOption)}>
            <option value="recency">Newest first</option>
            <option value="match">Best match</option>
          </select>
        </FilterField>
        <FilterField label="Min price">
          <NumberInput value={filters.minPrice} onChange={(v) => update("minPrice", v)} placeholder="$" width={110} />
        </FilterField>
        <FilterField label="Max price">
          <NumberInput value={filters.maxPrice} onChange={(v) => update("maxPrice", v)} placeholder="$" width={110} />
        </FilterField>
        <FilterField label="Min sqft">
          <NumberInput value={filters.minSqft} onChange={(v) => update("minSqft", v)} width={90} />
        </FilterField>
        <FilterField label="Beds (≥)">
          <NumberInput value={filters.minBeds} onChange={(v) => update("minBeds", v)} width={60} />
        </FilterField>
        <FilterField label="Baths (≥)">
          <NumberInput value={filters.minBaths} onChange={(v) => update("minBaths", v)} width={60} />
        </FilterField>
        <span style={{ flex: 1 }} />
        {hasHighlights && (
          <button
            onClick={() => clearHighlight()}
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }}
          >
            Clear highlights ({highlightedIds.size})
          </button>
        )}
        {isFiltered && (
          <button
            onClick={() => resetFilters()}
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text)" }}
          >
            Reset
          </button>
        )}
      </div>

      {q.isLoading && <p className="muted">Loading…</p>}
      {q.error && <p className="tag-error">Failed to load listings.</p>}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "380px 1fr",
          gap: 12,
          height: "calc(100vh - 230px)",
          minHeight: 480,
        }}
      >
        <Sidebar
          listings={filtered}
          hoverId={hoverId}
          highlightedIds={highlightedIds}
          onHover={setHoverId}
          onOpen={(id) => navigate(`/listings/${id}`)}
        />
        <div
          ref={containerRef}
          style={{
            borderRadius: 10,
            overflow: "hidden",
            border: "1px solid var(--border)",
            height: "100%",
          }}
        />
      </div>
    </>
  );
}

function Sidebar({
  listings,
  hoverId,
  highlightedIds,
  onHover,
  onOpen,
}: {
  listings: Listing[];
  hoverId: string | null;
  highlightedIds: Set<string>;
  onHover: (id: string | null) => void;
  onOpen: (id: string) => void;
}) {
  // Sort highlighted listings to the top so chat-mentioned listings are visible
  // without scrolling.
  const ordered = highlightedIds.size === 0
    ? listings
    : [...listings].sort((a, b) => {
        const ah = highlightedIds.has(a.listing_id) ? 1 : 0;
        const bh = highlightedIds.has(b.listing_id) ? 1 : 0;
        return bh - ah;
      });
  return (
    <div
      style={{
        overflowY: "auto",
        background: "var(--panel)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 8,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      {listings.length === 0 && (
        <p className="muted" style={{ padding: 12, margin: 0 }}>
          No listings match the current filters.
        </p>
      )}
      {ordered.map((l) => (
        <SidebarCard
          key={l.listing_id}
          listing={l}
          highlighted={l.listing_id === hoverId}
          chatHighlighted={highlightedIds.has(l.listing_id)}
          onHover={onHover}
          onOpen={onOpen}
        />
      ))}
    </div>
  );
}

function SidebarCard({
  listing: l,
  highlighted,
  chatHighlighted,
  onHover,
  onOpen,
}: {
  listing: Listing;
  highlighted: boolean;
  chatHighlighted: boolean;
  onHover: (id: string | null) => void;
  onOpen: (id: string) => void;
}) {
  const photo = l.photos?.[0] ?? l.hero_photo;
  const photoSrc = photo ? (photo.startsWith("http") ? photo : `/${photo}`) : null;
  const price = l.list_price ?? l.sale_price;
  const statusLabel = l.status === "sold" ? "Sold" : l.status === "pending" ? "Pending" : null;
  const recencyLabel = fmtRecencyLabel(l);
  const isNew = l.status !== "sold" && (daysOnMarket(l) ?? 999) <= 3;
  const assessmentColor: Record<string, string> = {
    underpriced: "var(--good)",
    fair: "var(--accent)",
    overpriced: "var(--bad)",
    unknown: "var(--muted)",
  };

  const borderColor = chatHighlighted
    ? "var(--good)"
    : highlighted
      ? "var(--accent)"
      : "transparent";
  const background = chatHighlighted
    ? "rgba(34, 197, 94, 0.08)"
    : highlighted
      ? "var(--bg)"
      : "transparent";
  return (
    <div
      onMouseEnter={() => onHover(l.listing_id)}
      onMouseLeave={() => onHover(null)}
      onClick={() => onOpen(l.listing_id)}
      style={{
        display: "flex",
        gap: 10,
        padding: 8,
        borderRadius: 8,
        cursor: "pointer",
        background,
        border: `1px solid ${borderColor}`,
        boxShadow: chatHighlighted ? "0 0 0 1px var(--good)" : undefined,
        transition: "background 0.1s",
      }}
    >
      <div
        style={{
          width: 100,
          height: 75,
          background: "var(--bg)",
          borderRadius: 6,
          flexShrink: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {photoSrc ? (
          <img
            src={photoSrc}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "var(--muted)",
              fontSize: 10,
            }}
          >
            no photo
          </div>
        )}
        {statusLabel && (
          <span
            style={{
              position: "absolute",
              top: 4,
              left: 4,
              background: l.status === "sold" ? "#6b7280" : "#f59e0b",
              color: "white",
              fontSize: 10,
              padding: "1px 6px",
              borderRadius: 3,
              fontWeight: 600,
            }}
          >
            {statusLabel}
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 16 }}>{fmtPriceFull(price)}</strong>
          {l.match_score != null && (
            <span
              title={matchScoreTooltip(l)}
              style={{
                fontSize: 10,
                background: matchScoreColor(l.match_score),
                color: "white",
                padding: "1px 6px",
                borderRadius: 8,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {l.match_score}
            </span>
          )}
          {l.ai_price_assessment && l.ai_price_assessment !== "unknown" && (
            <span
              title={l.ai_verdict || ""}
              style={{
                fontSize: 9,
                background: assessmentColor[l.ai_price_assessment],
                color: "white",
                padding: "1px 5px",
                borderRadius: 8,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 600,
              }}
            >
              {l.ai_price_assessment}
            </span>
          )}
          {isNew && (
            <span
              style={{
                fontSize: 9,
                background: "var(--accent)",
                color: "white",
                padding: "1px 5px",
                borderRadius: 8,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 600,
              }}
            >
              New
            </span>
          )}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          {(l.beds ?? "?")} bd · {(l.baths ?? "?")} ba ·{" "}
          {l.sqft != null ? l.sqft.toLocaleString() : "?"} sqft
        </div>
        {recencyLabel && (
          <div
            style={{
              fontSize: 11,
              marginTop: 2,
              fontWeight: 600,
              color: l.status === "sold" ? "var(--muted)" : "var(--accent)",
            }}
          >
            {recencyLabel}
          </div>
        )}
        <div
          style={{
            fontSize: 12,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {l.address}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span className="muted" style={{ fontSize: 11, flex: 1 }}>
            {l.town}
          </span>
          <InterestToggle listingId={l.listing_id} interest={l.interest} compact />
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span className="muted" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
  width,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  width?: number;
}) {
  return (
    <input
      type="number"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      style={{ width: width ?? 100, padding: "6px 8px" }}
    />
  );
}

const SCORE_COMPONENT_LABELS: Record<string, string> = {
  town: "Town",
  sqft: "Sqft",
  schools: "Schools",
  value: "Value",
  noise: "Street noise",
  amenities: "Amenities",
  garage: "Garage",
  lot_size: "Lot size",
  year_built: "Year built",
  beds: "Beds",
  baths: "Baths",
};

function matchScoreColor(score: number): string {
  if (score >= 75) return "var(--good)";
  if (score >= 50) return "var(--accent)";
  if (score >= 25) return "#f59e0b";
  return "var(--bad)";
}

function matchScoreTooltip(l: Listing): string {
  const bd = l.match_score_breakdown;
  if (!bd || Object.keys(bd).length === 0) return `Match score: ${l.match_score}`;
  const lines = Object.entries(bd)
    .sort(([, a], [, b]) => b.weight - a.weight)
    .map(
      ([key, c]) =>
        `${SCORE_COMPONENT_LABELS[key] ?? key}: ${Math.round(c.score * 100)} (weight ${c.weight})`,
    );
  return [`Match score: ${l.match_score}`, ...lines].join("\n");
}

function numOrNull(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function formatPrice(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function fmtPriceFull(n: number | null): string {
  if (n == null) return "—";
  return "$" + n.toLocaleString();
}

function renderPopupHtml(l: Listing): string {
  const photo = l.photos?.[0] ?? l.hero_photo;
  const photoSrc = photo ? (photo.startsWith("http") ? photo : `/${photo}`) : null;
  const price = formatPrice(l.list_price ?? l.sale_price);
  const beds = l.beds ?? "?";
  const baths = l.baths ?? "?";
  const sqft = l.sqft != null ? l.sqft.toLocaleString() : "?";
  const statusLabel =
    l.status === "sold" && l.sale_price != null
      ? `Sold ${price}`
      : l.status === "pending"
        ? `Pending ${price}`
        : price;
  const recencyLabel = fmtRecencyLabel(l);
  const recencyColor = l.status === "sold" ? "var(--muted)" : "var(--accent)";

  return `
    <div class="listing-popup-inner">
      ${photoSrc ? `<img src="${escapeHtml(photoSrc)}" alt="" />` : ""}
      <div class="lp-body">
        <div class="lp-price">${escapeHtml(statusLabel)}</div>
        <div class="lp-stats">${beds} bd · ${baths} ba · ${sqft} sqft</div>
        <div class="lp-addr">${escapeHtml(l.address)}</div>
        ${recencyLabel ? `<div class="lp-stats" style="margin-top:2px;color:${recencyColor};font-weight:600;">${escapeHtml(recencyLabel)}</div>` : ""}
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  const div = document.createElement("div");
  div.textContent = s;
  return div.innerHTML;
}
